// /partners — listado de partners del sistema.
//
// Read-only desde cualquier rol. Al click → /partners/:id/edit (M9.5 tabs:
// General, Branding, Plans, Clients).

import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { listPartners } from '@/lib/api';
import { useSession } from '@/hooks/useSession';
import { canWrite } from '@/lib/access';

function statusBadge(status: string): JSX.Element {
  const cls =
    status === 'active'
      ? 'bg-status-success/20 text-status-success'
      : status === 'suspended'
        ? 'bg-status-warning/20 text-status-warning'
        : 'bg-ink-muted/20 text-ink-muted';
  return <span className={`text-xs rounded-full px-2 py-0.5 font-medium ${cls}`}>{status}</span>;
}

export default function PartnersList(): JSX.Element {
  const q = useQuery({ queryKey: ['admin-partners'], queryFn: listPartners });
  const session = useSession();
  const canCreate = canWrite(session, 'partners');

  return (
    <div className="space-y-6">
      <header className="flex items-start justify-between gap-4">
        <div>
          <h1 data-help-page="partners.page" data-help="partners.page" className="text-2xl font-semibold">Partners</h1>
          <p className="text-sm text-ink-muted">
            Tenants del sistema. Click para editar branding, TTLs, feature flags, plans y clients.
          </p>
        </div>
        {canCreate ? (
          <Link
            to="/partners/new"
            data-help="partners.new"
            className="shrink-0 rounded-md bg-brand-primary px-4 py-2 text-sm font-medium text-white hover:bg-brand-primary-hover"
          >
            + Nuevo partner
          </Link>
        ) : null}
      </header>

      {q.isLoading ? <p className="text-sm text-ink-muted">Cargando…</p> : null}
      {q.isError ? (
        <div role="alert" className="rounded-lg border border-status-error/30 bg-status-error/10 p-3 text-sm text-status-error">
          No pudimos cargar la lista de partners.
        </div>
      ) : null}

      {q.data ? (
        <div className="bg-surface dark:bg-surface-dark rounded-xl border border-surface-border dark:border-surface-dark-border overflow-hidden">
          <ul className="divide-y divide-surface-border dark:divide-surface-dark-border">
            {q.data.map((p) => (
              <li key={p.partner_id} className="flex items-center gap-3 px-5 py-3">
                <div className="flex-1">
                  <Link
                    to={`/partners/${p.partner_id}/edit`}
                    className="font-medium hover:underline"
                  >
                    {p.display_name}
                  </Link>
                  <p className="text-xs text-ink-muted font-mono">{p.partner_id}</p>
                </div>
                {statusBadge(p.status)}
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </div>
  );
}
